import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import CommonButton from "../../components/common/CommonButton";
import { fontFamily } from "@/styles/font";

const NicknameInput: React.FC = () => {
    const navigate = useNavigate();
    const [nickname, setNickname] = useState("");
    const [error, setError] = useState("");
    const maxLength = 10;

    // 컴포넌트 마운트 시 세션 스토리지에서 닉네임 불러오기
    useEffect(() => {
        const savedNickname = sessionStorage.getItem("nickname");
        if (savedNickname) {
            setNickname(savedNickname);
        }
    }, []);

    const handleNicknameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        // 닉네임 길이 검증
        if (value.length > maxLength) {
            setError(`닉네임은 ${maxLength}자를 초과할 수 없습니다.`);
            return;
        }

        setNickname(value);
        setError("");
    };

    const handleBack = () => {
        // 닉네임 저장 후 이전 페이지로 이동
        if (nickname.trim()) {
            sessionStorage.setItem("nickname", nickname);
        }

        // 약관 동의 데이터는 유지하고 이전 페이지로 이동
        navigate(-1);
    };

    const handleNext = () => {
        if (nickname.trim()) {
            // 닉네임 세션 스토리지에 저장
            sessionStorage.setItem("nickname", nickname);

            // 다음 단계로 이동
            navigate("/join/userinfo");
        } else {
            setError("닉네임을 입력해주세요.");
        }
    };

    return (
        <Container>
            {/* 네비게이션 바 */}
            <NavigationBar>
                <TextActionFrame>
                    <IconButton onClick={handleBack}>
                        <BackIcon />
                    </IconButton>
                </TextActionFrame>
                <LargeTextContainer>
                    <Title>사용할 닉네임을 입력해주세요</Title>
                    <SubTitle>닉네임은 언제든지 변경할 수 있어요.</SubTitle>
                </LargeTextContainer>
            </NavigationBar>

            {/* 입력 영역 */}
            <ContentSection>
                <InputContainer>
                    <TextFieldContainer>
                        <LabelContainer>
                            <PlaceholderText
                                value={nickname}
                                onChange={handleNicknameChange}
                                placeholder="닉네임을 입력해주세요"
                                maxLength={maxLength}
                            />
                            <CharacterCounter>
                                <CurrentCount>{nickname.length}</CurrentCount>
                                <MaxCount>/{maxLength}</MaxCount>
                            </CharacterCounter>
                        </LabelContainer>
                        <HelperText error={!!error}>{error || " "}</HelperText>
                    </TextFieldContainer>
                </InputContainer>

                {/* 다음 버튼 */}
                <ButtonWrapper>
                    <CommonButton text="다음" enabled={nickname.trim().length > 0} onClick={handleNext} />
                </ButtonWrapper>
            </ContentSection>
        </Container>
    );
};

// 스타일 컴포넌트
const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    min-width: 320px;
    max-width: 430px;
    height: calc(100vh - 40px);
    margin: 0 auto;
    background: #ffffff;
    font-family: ${fontFamily.base};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-size: clamp(14px, 2.8vw, 18px);
    line-height: 1.5;
    font-weight: 400;
    overflow-x: hidden;
`;

const NavigationBar = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    height: 140px;
    background: #ffffff;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const TextActionFrame = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0px;
    gap: 262px;
    width: 100%;
    height: 48px;
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;
`;

const IconButton = styled.button`
    width: 48px;
    height: 48px;
    background: transparent;
    border: none;
    cursor: pointer;
    position: relative;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const BackIcon = styled.div`
    position: absolute;
    width: 24px;
    height: 24px;
    left: calc(50% - 24px / 2);
    top: calc(50% - 24px / 2);
    background-image: url("/icon/chevron-left.png");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
`;

const LargeTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    gap: 8px;
    width: 100%;
    height: 62px;
    background: #ffffff;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

const Title = styled.h2`
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    color: #1f1f1f;
    margin: 0;
`;

const SubTitle = styled.p`
    width: 100%;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 2.4vw, 16px);
    line-height: 1.5;
    color: #a9b5c0;
    margin: 0;
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
    width: 100%;
    height: calc(100vh - 188px);
    border-radius: 0px;
    flex: none;
    order: 2;
    align-self: stretch;
    flex-grow: 0;
    box-sizing: border-box;
`;

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    width: 100%;
    height: 64px;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const TextFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0px;
    gap: 4px;
    width: 100%;
    height: 64px;
    flex: none;
    order: 0;
    flex-grow: 0;
`;

const LabelContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    gap: 8px;
    isolation: isolate;
    width: 100%;
    height: 48px;
    background: #ffffff;
    border: 1px solid #e8eef2;
    border-radius: 8px;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
    position: relative;
`;

const PlaceholderText = styled.input`
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(14px, 2.8vw, 18px);
    line-height: 1.5;
    color: #464a4d;
    background: transparent;

    &::placeholder {
        color: #a9b5c0;
    }
`;

const CharacterCounter = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    gap: 0px;
    width: auto;
    height: 24px;
    flex: none;
    order: 1;
    flex-grow: 0;
`;

const CurrentCount = styled.span`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 2.4vw, 16px);
    line-height: 1.5;
    color: #464a4d;
`;

const MaxCount = styled.span`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 2.4vw, 16px);
    line-height: 1.5;
    color: #a9b5c0;
`;

const HelperText = styled.p<{ error?: boolean }>`
    font-family: ${fontFamily.base};
    font-style: normal;
    font-weight: 400;
    font-size: clamp(12px, 2.4vw, 16px);
    line-height: 1.5;
    color: ${(props) => (props.error ? "#FF5252" : "#a9b5c0")};
    margin: 0;
    padding-left: 8px;
`;
const ButtonWrapper = styled.div`
    width: 100%;
    margin-top: auto;
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;
`;

export default NicknameInput;
